import React, { useContext, useState } from "react";
import Page from "~src/components/Page";
import { Container, Box, Tab } from "@mui/material";
import HeaderBreadcrumbs from "~src/components/HeaderBreadcrumbs";
import { PATH_COMPETITOR_ANALYTICS } from "~src/routes/paths";
import useSettings from "~src/hooks/useSettings";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import CompetitorAnalytics from "~src/sections/competitorAnalytics/CompetitorAnalytics";
import BrandMapping from "~src/sections/brandMapping";
import { BrandComparisonContext } from "~src/contexts/BrandComparisonContext";
import useBrandMapping from "~src/hooks/useBrandMapping";
import MyTrackedStores from "~src/sections/myTrackedStores";
import useBrandMappingV2 from "~src/hooks/useBrandMappingV2";
import useBrandMappingV3 from "~src/hooks/useBrandMappingV3";
import { MapOfBrands } from "~src/types/brandMapping";
import useAuth from "~src/hooks/useAuth";
import { BrandMappingContext } from "~src/contexts/BrandMappingContext";
import { BrandMappingContextV2 } from "~src/contexts/BrandMappingContextV2";
import { BrandMappingContextV3 } from "~src/contexts/BrandMappingContextV3";

const tabsConfig = [
  {
    label: "Map of Brands",
    value: "map_of_brands",
  },
  {
    label: "Map of Brands (V2)",
    subtitle: "BE pagination with brand exclude feature",
    value: "map_of_brands_v2",
  },
  {
    label: "Map of Brands (V3)",
    subtitle: "FE pagination with brand exclude feature",
    value: "map_of_brands_v3",
  },
  {
    label: "Check Competitor",
    value: "check_competitor",
  },
  {
    label: "My Tracked Stores",
    value: "my_tracked_stores",
  },
];

export default function Dashboard() {
  const { user } = useAuth();
  const is_superuser = user?.is_superuser;
  const { themeStretch } = useSettings();
  const [tab, setTab] = useState(tabsConfig[0].value);
  const [brandMapSearchText, setBrandMapSearchText] = useState<
    string | undefined
  >();
  const [brandMapSearchTextV2, setBrandMapSearchTextV2] = useState<
    string | undefined
  >();
  const [brandMapSearchTextV3, setBrandMapSearchTextV3] = useState<
    string | undefined
  >();
  const [exactMatch, setExactMatch] = useState(true);
  const [exactMatchV2, setExactMatchV2] = useState(true);
  const [exactMatchV3, setExactMatchV3] = useState(true);

  const [brandToRemoveV2, setBrandToRemoveV2] = useState<
    MapOfBrands[] | undefined
  >([]);
  const [brandToRemoveV3, setBrandToRemoveV3] = useState<
    MapOfBrands[] | undefined
  >([]);

  const { loadMore, geographicalSummary, loading, initialLoading } =
    useBrandMapping(brandMapSearchText, exactMatch);

  const {
    loadMoreV2,
    geographicalSummaryV2,
    filtersV2,
    setFiltersV2,
    loadingV2,
    initialLoadingV2,
    brandCountriesV2,
  } = useBrandMappingV2(brandMapSearchTextV2, exactMatchV2, brandToRemoveV2);

  const {
    loadMoreV3,
    geographicalSummaryV3,
    filtersV3,
    setFiltersV3,
    loadingV3,
    initialLoadingV3,
    brandCountriesV3,
  } = useBrandMappingV3(brandMapSearchTextV3, exactMatchV3, brandToRemoveV3);

  const { mapOfBrandsContext: mappedBrands } =
    useContext(BrandMappingContext) || {};
  const { mapOfBrandsContext: mappedBrandsV2 } =
    useContext(BrandMappingContextV2) || {};
  const { mapOfBrandsContext: mappedBrandsV3 } =
    useContext(BrandMappingContextV3) || {};

  const { selectedBrands } = useContext(BrandComparisonContext) || {};

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  return (
    <Page title={tabsConfig.find((t) => t.value === tab)?.label || "Dashboard"}>
      <Container maxWidth={themeStretch ? false : "xl"}>
        <HeaderBreadcrumbs
          heading={
            tabsConfig.find((t) => t.value === tab)?.label || "Dashboard"
          }
          subtitle="Find where your product category is sold worldwide in seconds. Uncover new markets and opportunities with TrendRocket."
          homeIcon="competitor_analytics"
          links={[
            {
              name:
                tabsConfig.find((t) => t.value === tab)?.label || "Dashboard",
              href: PATH_COMPETITOR_ANALYTICS.root,
            },
          ]}
        />
        <TabContext value={tab}>
          <Box>
            <TabList onChange={handleChange} aria-label="lab API tabs example">
              {tabsConfig.map((tabItem) => {
                if (
                  !is_superuser &&
                  (tabItem.value === "map_of_brands_v2" ||
                    tabItem.value === "map_of_brands_v3")
                ) {
                  return null;
                }
                return (
                  <Tab
                    key={tabItem.value}
                    label={
                      `${tabItem.label}` +
                      (tabItem.value === "check_competitor"
                        ? ` (${selectedBrands?.length || 0}/5)`
                        : "")
                    }
                    value={tabItem.value}
                  />
                );
              })}
            </TabList>
          </Box>
          {tabsConfig.map(
            (tabItem, index) =>
              tabItem.value === tab && (
                <TabPanel key={index} value={tabItem.value}>
                  {tabItem.value == "check_competitor" ? (
                    <CompetitorAnalytics />
                  ) : tabItem.value === "map_of_brands" ? (
                    <BrandMapping
                      tabVersion={"v1"}
                      exactMatch={exactMatch}
                      setExactMatch={setExactMatch}
                      searchText={brandMapSearchText}
                      setSearchText={setBrandMapSearchText}
                      mappedBrands={mappedBrands}
                      loadMore={loadMore}
                      initialLoading={initialLoading}
                      geographicalSummary={geographicalSummary}
                      loading={loading}
                    />
                  ) : tabItem.value === "map_of_brands_v2" ? (
                    <BrandMapping
                      tabVersion={"v2"}
                      setBrandToRemove={setBrandToRemoveV2}
                      exactMatch={exactMatchV2}
                      setExactMatch={setExactMatchV2}
                      searchText={brandMapSearchTextV2}
                      setSearchText={setBrandMapSearchTextV2}
                      mappedBrands={mappedBrandsV2}
                      filters={filtersV2}
                      onFiltersUpdated={setFiltersV2}
                      brandCountries={brandCountriesV2}
                      loadMore={loadMoreV2}
                      initialLoading={initialLoadingV2}
                      geographicalSummary={geographicalSummaryV2}
                      loading={loadingV2}
                    />
                  ) : tabItem.value === "map_of_brands_v3" ? (
                    <BrandMapping
                      tabVersion={"v3"}
                      setBrandToRemove={setBrandToRemoveV3}
                      exactMatch={exactMatchV3}
                      setExactMatch={setExactMatchV3}
                      searchText={brandMapSearchTextV3}
                      setSearchText={setBrandMapSearchTextV3}
                      mappedBrands={mappedBrandsV3}
                      filters={filtersV3}
                      onFiltersUpdated={setFiltersV3}
                      brandCountries={brandCountriesV3}
                      loadMore={loadMoreV3}
                      initialLoading={initialLoadingV3}
                      geographicalSummary={geographicalSummaryV3}
                      loading={loadingV3}
                    />
                  ) : (
                    <MyTrackedStores />
                  )}
                </TabPanel>
              ),
          )}
        </TabContext>
      </Container>
    </Page>
  );
}
